import { memo, useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { FOLLOWER_TIERS, bucketize, formatCount } from "./influencerFormat";
import { platformLabel } from "./platforms";

// Mirrors PLAN_DESCRIPTION_MAX in pluginbackendnode/models/plan.js.
const DESCRIPTION_MAX = 2000;
const TOP_N = 8;
const ACCENT = "#FF5200";

// category is a comma-joined multi-tag string, language an array, location one
// exact string ("Mumbai, Maharashtra, India" — same as the location filter).
const TAG_READERS = {
  category: (inf) => String(inf.category || "").split(","),
  language: (inf) => (Array.isArray(inf.language) ? inf.language : [inf.language]),
  location: (inf) => [inf.location],
  tier: (inf) => [FOLLOWER_TIERS.find((t) => t.value === bucketize(inf.followers, FOLLOWER_TIERS))?.label],
};

// -> [{ label, count }] sorted by count desc.
const tally = (creators, field) => {
  const counts = new Map();
  creators.forEach((inf) =>
    new Set(TAG_READERS[field](inf).map((t) => String(t ?? "").trim()).filter(Boolean))
      .forEach((t) => counts.set(t, (counts.get(t) || 0) + 1))
  );
  return [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
};

// The same creator can sit in several lists of one plan — count them once.
const uniqueCreators = (influencers) => {
  const seen = new Map();
  influencers.forEach((inf) => {
    const key = inf.rapidInfluencerId || (inf.username ? `${inf.platform}:${inf.username}` : inf.id);
    if (!seen.has(key)) seen.set(key, inf);
  });
  return [...seen.values()];
};

const shortTierLabel = (label) => label.split(" (")[0];

const BreakdownCard = ({ title, rows, total }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{title}</Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">Not available yet</Typography>
    ) : (
      <>
        {rows.slice(0, TOP_N).map(({ label, count }) => (
          <Box key={label} sx={{ mb: 1.25 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" noWrap title={label}>{label}</Typography>
              <Typography variant="body2" color="text.secondary">{count}</Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: "#FFF0ED", mt: 0.5 }}>
              <Box sx={{ height: "100%", borderRadius: 3, bgcolor: ACCENT, width: `${total ? Math.min(100, (count / total) * 100) : 0}%` }} />
            </Box>
          </Box>
        ))}
        {rows.length > TOP_N && (
          <Typography variant="caption" color="text.secondary">+{rows.length - TOP_N} more</Typography>
        )}
      </>
    )}
  </Paper>
);

const Stat = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography sx={{ fontWeight: 600, fontSize: 20 }}>{value}</Typography>
  </Box>
);

// The plan page's "Summary" view: an editable brief of what the plan is about,
// plus a read-only breakdown of the creators across ALL of the plan's lists.
const PlanOverview = ({ description, influencers, lists, onSaveDescription, isSaving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description);

  useEffect(() => {
    if (!editing) setDraft(description);
  }, [description, editing]);

  const stats = useMemo(() => {
    const creators = uniqueCreators(influencers);
    const withFollowers = creators.filter((i) => i.followers != null);
    const withEr = creators.filter((i) => i.engagementRate != null);
    return {
      total: creators.length,
      totalFollowers: withFollowers.reduce((sum, i) => sum + Number(i.followers), 0),
      avgEr: withEr.length ? withEr.reduce((sum, i) => sum + Number(i.engagementRate), 0) / withEr.length : null,
      tiers: tally(creators, "tier"),
      categories: tally(creators, "category"),
      locations: tally(creators, "location"),
      languages: tally(creators, "language"),
      memberships: influencers.length,
      lists: lists
        .map((l) => ({ label: `${l.name} · ${platformLabel(l.platform)}`, count: l.influencerCount }))
        .sort((a, b) => b.count - a.count),
    };
  }, [influencers, lists]);

  // One-line auto summary, e.g. "20 creators across 2 lists — mostly Micro (12), top categories: Food, Lifestyle."
  const autoSummary = useMemo(() => {
    if (!stats.total) return "No creators added yet.";
    const parts = [`${stats.total} creator${stats.total === 1 ? "" : "s"} across ${lists.length} list${lists.length === 1 ? "" : "s"}`];
    const topTier = stats.tiers[0];
    if (topTier) parts.push(`mostly ${shortTierLabel(topTier.label)} (${topTier.count})`);
    if (stats.categories.length) parts.push(`top categories: ${stats.categories.slice(0, 3).map((c) => c.label).join(", ")}`);
    return `${parts.join(" — ")}.`;
  }, [stats, lists.length]);

  const handleSave = () =>
    onSaveDescription(draft.trim(), () => setEditing(false));

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>About this plan</Typography>
          {!editing && (
            <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)} sx={{ textTransform: "none", color: ACCENT }}>
              {description ? "Edit" : "Add"}
            </Button>
          )}
        </Box>
        {editing ? (
          <>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, DESCRIPTION_MAX))}
              placeholder="Campaign goal, target audience, kind of creators wanted, deliverables, timelines…"
              helperText={`${draft.length}/${DESCRIPTION_MAX}`}
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
              <Button size="small" onClick={() => setEditing(false)} disabled={isSaving} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || draft.trim() === description}
                sx={{ textTransform: "none", boxShadow: "none", bgcolor: ACCENT, "&:hover": { bgcolor: "#E64A00" } }}
              >
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </Box>
          </>
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: description ? "text.primary" : "text.secondary" }}>
            {description || "No description yet — add what this plan is about so everyone on the team has the context."}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{autoSummary}</Typography>
        <Box sx={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          <Stat label="Creators" value={formatCount(stats.total)} />
          <Stat label="Combined followers" value={formatCount(stats.totalFollowers)} />
          <Stat label="Avg engagement rate" value={stats.avgEr == null ? "—" : `${stats.avgEr.toFixed(2)}%`} />
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 2 }}>
        <BreakdownCard title="Follower range" rows={stats.tiers} total={stats.total} />
        <BreakdownCard title="Categories" rows={stats.categories} total={stats.total} />
        <BreakdownCard title="Lists & platforms" rows={stats.lists} total={stats.memberships} />
        <BreakdownCard title="Locations" rows={stats.locations} total={stats.total} />
        <BreakdownCard title="Languages" rows={stats.languages} total={stats.total} />
      </Box>
    </Box>
  );
};

export default memo(PlanOverview);
